import styled from 'styled-components'
import {
  ListItem,
  StatusBadge,
  ImageBouquet,
} from '@lmc/ui-kit/src/dataDisplay'
import { Avatar, ThreeDots, theme } from '@lmc/ui-kit'
import { Name, TaskItemResponse } from '@lmc/ts-common'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { Popover } from 'antd'
import TaskModal from '../TaskModals'

type TaskProps = {
  task: TaskItemResponse
  refetchDailyTasks: () => Promise<any>
  refetchTasks: () => Promise<any>
}

const AVATAR_SIZE = 40

const Task = ({ task, refetchDailyTasks, refetchTasks }: TaskProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const [openMenuPopover, setOpenMenuPopover] = useState(false)
  const [openMembersPopover, setOpenMembersPopover] = useState(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [modalType, setModalType] = useState('')
  const { calculatedStatus, residentData, assignee, title, assigneeData } = task

  const handleOpenModal = (modalType: string) => {
    setModalType(modalType)
    setShowModal(true)
    setOpenMenuPopover(false)
  }

  const handleOpenMenuChange = (newOpen: boolean) => {
    setOpenMenuPopover(newOpen)
  }

  const getName = (name: Name | string) => {
    if (typeof name === 'string') {
      return name
    }

    return `${name.first} ${name.last}`
  }

  return (
    <>
      <Wrapper
        icon={
          residentData && (
            <TaskIcon>
              <Avatar size={AVATAR_SIZE} url={residentData.picture} />
            </TaskIcon>
          )
        }
        title={
          <TaskTitle level={5} weight="medium">
            {residentData
              ? // some ts common issue here
                getName(residentData.name)
              : title}
          </TaskTitle>
        }
        rightChildren={
          <TaskRightChildren>
            <DesktopStatusBadge status={calculatedStatus} leftText />
            <MobileStatusBadge status={calculatedStatus} />
            {assignee?.length && (
              <ImageBouquet
                images={assigneeData.map((asignee) => asignee.picture)}
                maximumRenderedItems={3}
                popoverContent={
                  <>
                    <MembersPopoverText level={2}>
                      {t(toDosConstants.TEAM_MEMBERS)}
                    </MembersPopoverText>
                    {assigneeData.map((asignee) => (
                      <MembersPopoverOption key={asignee.id}>
                        <Avatar size={18} url={asignee.picture} />
                        <Text>{asignee.name}</Text>
                      </MembersPopoverOption>
                    ))}
                  </>
                }
                open={openMembersPopover}
                openChangeFunction={setOpenMembersPopover}
              />
              // keeping this here - going to be used in next iteration of ToDos
              // <Popover
              //   placement="leftTop"
              //   showArrow={false}
              //   content={
              //     <>
              //       <MembersPopoverText level={2}>
              //         {t(toDosConstants.TEAM_MEMBERS)}
              //       </MembersPopoverText>
              //       <AnyMemberDescription>
              //         <AnyMemberText>
              //           {t(toDosConstants.ANY_TEAM_MEMBERS)}
              //         </AnyMemberText>
              //       </AnyMemberDescription>
              //     </>
              //   }
              //   trigger="hover"
              // >
              //   <Text>{t(toDosConstants.ANYONE)}</Text>
              // </Popover>
            )}
            <Popover
              placement="leftTop"
              onOpenChange={handleOpenMenuChange}
              open={openMenuPopover}
              showArrow={false}
              content={
                <PopoverContent>
                  <PopoverOption onClick={() => handleOpenModal('status')}>
                    <Text>{t(toDosConstants.CHANGE_STATUS)}</Text>
                  </PopoverOption>
                  {/* <PopoverOption onClick={() => handleOpenModal('team')}>
                    <Text>{t(toDosConstants.CHANGE_TEAM_MEMBERS)}</Text>
                  </PopoverOption> keeping this comment - going to be used */}
                  <PopoverOption onClick={() => handleOpenModal('snooze')}>
                    <Text>{t(toDosConstants.SNOOZE)}</Text>
                  </PopoverOption>
                  <PopoverOption onClick={() => handleOpenModal('delete')}>
                    <Text>{t(toDosConstants.DELETE)}</Text>
                  </PopoverOption>
                </PopoverContent>
              }
              trigger="click"
            >
              <MenuContainer>
                <ThreeDots
                  color={
                    openMenuPopover
                      ? theme.color.status.primary
                      : theme.color.neutral.color3
                  }
                />
              </MenuContainer>
            </Popover>
          </TaskRightChildren>
        }
      />
      <TaskModal
        task={task}
        isModalVisible={showModal}
        setIsModalVisible={setShowModal}
        modalType={modalType}
        refetchDailyTasks={refetchDailyTasks}
        refetchTasks={refetchTasks}
      />
    </>
  )
}

const Wrapper = styled(ListItem)`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 16px;
  padding: 12px 16px;
  margin-top: 12px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 4px 8px;
  }
`

const TaskRightChildren = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    gap: 8px;
  }
`

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const TaskIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50px;
  overflow: hidden;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const TaskTitle = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const PopoverContent = styled.div``

const MembersPopoverText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const MobileStatusBadge = styled(StatusBadge)`
  display: none;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: flex;
  }
`
const DesktopStatusBadge = styled(StatusBadge)`
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const PopoverOption = styled.div`
  padding: 4px;
  border-radius: 9px;
  &:hover {
    background-color: ${({ theme }) => theme.color.neutral.color9};
  }
`

const MembersPopoverOption = styled.div`
  display: flex;
  cursor: pointer;
  gap: 4px;
  padding: 4px;
  border-radius: 9px;
`

// keeping this as a comment, it's going to be used in the next iteration
// const AnyMemberDescription = styled.div`
//   padding: 4px;
// `

// const AnyMemberText = styled(Text)`
//   color: ${({ theme }) => theme.color.neutral.color5};
// `

export default Task
